import { memo, useEffect, useRef } from 'react';
import { createFrameCapturer } from './frame-capturer.tsx';
import { createScrollDataCollector } from './scroll-data-collector.tsx';
import { processScrollingData, type Measures } from './process-scrolling-data.tsx';

const bufferingInterval = 1000 * 60 * 1; // 1 minute

// After that many ms without consecutive scroll events
// we assume the user is no longer scrolling the view
const scrollStopThresholdTime = 100;

type OnMeasuresCollected = (measures: Measures) => void;

type ObserverProps = {
	scrollingContainer: HTMLElement;
	onMeasuresCollected: OnMeasuresCollected;
};

export const ObserverNew = memo<ObserverProps>(
	({ scrollingContainer, onMeasuresCollected }: ObserverProps) => {
		// We use ref here to not have to put onMeasuresCollected as a dependency
		// for the useEffect below. That way the scrollDataCollector callback
		// will use always the latest onMeasuresCollected callback value.
		const onMeasuresCollectedRef = useRef(onMeasuresCollected);
		onMeasuresCollectedRef.current = onMeasuresCollected;

		useEffect(() => {
			const frameCapturer = createFrameCapturer();
			const scrollDataCollector = createScrollDataCollector((scrolls) => {
				onMeasuresCollectedRef.current(processScrollingData(scrolls));
			}, bufferingInterval);

			let scrollTimeoutId: ReturnType<typeof setTimeout>;
			const handleScroll = () => {
				if (frameCapturer.isIdle()) {
					frameCapturer.startCapturingFrames();
				}

				clearTimeout(scrollTimeoutId);
				scrollTimeoutId = setTimeout(() => {
					scrollDataCollector.collect(frameCapturer.stopCapturingFrames());
				}, scrollStopThresholdTime);
			};

			scrollingContainer.addEventListener('scroll', handleScroll);

			return () => {
				clearTimeout(scrollTimeoutId);
				scrollingContainer.removeEventListener('scroll', handleScroll);

				scrollDataCollector.dispose();
				frameCapturer.dispose();
			};
		}, [scrollingContainer]);

		return null;
	},
);
